import { SerasaExperianData, AnaliseCadastral, Negociacao } from '../../types';

// ── Weight configuration (must sum to 1.0) ──
const PESOS = {
  score_serasa: 0.25,           // Serasa score 0-1000
  dividas_totais: 0.20,         // Total debt burden
  protestos_cheques: 0.15,      // Protestos + Cheques SF
  acoes_restritivos: 0.10,      // Ações judiciais + PEFIN/REFIN/CCF
  situacao_fiscal: 0.10,        // Receita Federal status
  consistencia_ucs: 0.10,       // UC validation consistency
  concentracao_consumo: 0.10,   // Consumption concentration risk
};

// ── Factor calculators with linear interpolation ──

function pontuarScoreSerasa(score?: number): number {
  if (score == null) return 50;
  // Linear: 0→0, 300→25, 500→50, 700→75, 1000→100
  return Math.min(100, Math.max(0, Math.round((score / 1000) * 100)));
}

function pontuarDividasTotais(serasa: SerasaExperianData): number {
  // Sum all valor fields
  const totalValor =
    (serasa.valor_anotacoes_negativas ?? 0) +
    (serasa.valor_dividas_comerciais ?? 0) +
    (serasa.valor_acoes_judiciais ?? 0) +
    (serasa.valor_dividas_bancarias ?? 0) +
    (serasa.valor_total_protestos ?? 0) +
    (serasa.valor_total_cheques_sf ?? 0);

  if (totalValor === 0) return 100;
  // Exponential decay: R$1k→85, R$5k→60, R$20k→30, R$50k→10
  const decay = Math.exp(-totalValor / 15000);
  return Math.round(Math.max(0, Math.min(100, decay * 100)));
}

function pontuarProtestosCheques(serasa: SerasaExperianData): number {
  const qtdProtestos = serasa.quantidade_protestos ?? 0;
  const qtdCheques = serasa.quantidade_cheques_sf ?? 0;
  const total = qtdProtestos + qtdCheques;

  if (total === 0) return 100;
  if (total <= 2) return 70;
  if (total <= 5) return 40;
  if (total <= 10) return 15;
  return 0;
}

function pontuarAcoesRestritivos(serasa: SerasaExperianData): number {
  let penalidade = 0;

  // Each restrictive flag = -25 points
  if (serasa.pefin) penalidade += 25;
  if (serasa.refin) penalidade += 25;
  if (serasa.ccf) penalidade += 20;

  // Ações judiciais: -15 per action, max -30
  const acoes = serasa.acoes_judiciais ?? 0;
  penalidade += Math.min(30, acoes * 15);

  return Math.max(0, 100 - penalidade);
}

function pontuarSituacaoFiscal(serasa: SerasaExperianData): number {
  const situacao = serasa.situacao_receita_federal;
  switch (situacao) {
    case 'Regular': return 100;
    case 'Pendente': return 50;
    case 'Suspensa': return 25;
    case 'Cancelada':
    case 'Nula':
    case 'Inapta': return 0;
    default: return 60; // no data = slightly below neutral
  }
}

function pontuarConsistenciaUCs(analise: Partial<AnaliseCadastral>, negociacao?: Negociacao): number {
  const ucs = negociacao?.unidades_consumidoras || [];
  if (ucs.length === 0) return 50;

  // Check conferencia documental completeness
  const conf = analise.conferencia_docs;
  if (!conf) return 40;

  // Count validated items in termo_adesao
  const termo = conf.termo_adesao || {};
  const termoFields = Object.values(termo).filter(v => typeof v === 'boolean');
  const termoOk = termoFields.filter(v => v === true).length;
  const termoTotal = termoFields.length || 1;

  // Ratio of validated items determines score
  const ratio = termoOk / termoTotal;
  return Math.round(ratio * 100);
}

function pontuarConcentracaoConsumo(analise: Partial<AnaliseCadastral>, negociacao?: Negociacao): number {
  const compensavel = analise.consumo_compensavel_kwh;
  const consumoMedio = negociacao?.consumo_medio_kwh;

  // If no data, neutral
  if (!compensavel || !consumoMedio || consumoMedio === 0) return 60;

  // Ratio of compensavel vs medio — higher is better (good generation capacity)
  const ratio = compensavel / consumoMedio;

  if (ratio >= 1.0) return 100;  // covers 100%+ of consumption
  if (ratio >= 0.7) return 85;
  if (ratio >= 0.4) return 65;
  if (ratio >= 0.2) return 45;
  return 25;
}

// ── Breakdown interface ──
export interface FatorRisco {
  nome: string;
  peso: number;
  pontuacao: number;
  contribuicao: number;
}

export interface ResultadoMotorRisco {
  score: number;
  classificacao: string;
  fatores: FatorRisco[];
}

// ── Main calculator ──
export function calcularMotorRisco(
  serasa: SerasaExperianData | undefined,
  analise: Partial<AnaliseCadastral>,
  negociacao?: Negociacao
): ResultadoMotorRisco {
  const s = serasa || {};

  const fatores: FatorRisco[] = [
    {
      nome: 'Score Serasa',
      peso: PESOS.score_serasa,
      pontuacao: pontuarScoreSerasa(s.score),
      contribuicao: 0,
    },
    {
      nome: 'Dívidas Totais',
      peso: PESOS.dividas_totais,
      pontuacao: pontuarDividasTotais(s),
      contribuicao: 0,
    },
    {
      nome: 'Protestos / Cheques',
      peso: PESOS.protestos_cheques,
      pontuacao: pontuarProtestosCheques(s),
      contribuicao: 0,
    },
    {
      nome: 'Ações / Restritivos',
      peso: PESOS.acoes_restritivos,
      pontuacao: pontuarAcoesRestritivos(s),
      contribuicao: 0,
    },
    {
      nome: 'Situação Fiscal',
      peso: PESOS.situacao_fiscal,
      pontuacao: pontuarSituacaoFiscal(s),
      contribuicao: 0,
    },
    {
      nome: 'Conferência Documental',
      peso: PESOS.consistencia_ucs,
      pontuacao: pontuarConsistenciaUCs(analise, negociacao),
      contribuicao: 0,
    },
    {
      nome: 'Consumo / Geração',
      peso: PESOS.concentracao_consumo,
      pontuacao: pontuarConcentracaoConsumo(analise, negociacao),
      contribuicao: 0,
    },
  ];

  // Calculate weighted contributions
  fatores.forEach(f => {
    f.contribuicao = Math.round(f.pontuacao * f.peso);
  });

  const score = fatores.reduce((acc, f) => acc + f.contribuicao, 0);
  const classificacao = getClassificacaoFromScore(score);

  return { score, classificacao, fatores };
}

// ── Classification helpers ──
function getClassificacaoFromScore(score: number): string {
  if (score >= 80) return 'Excelente';
  if (score >= 60) return 'Bom';
  if (score >= 40) return 'Médio';
  if (score >= 20) return 'Alto';
  return 'Crítico';
}

export function getClassificacaoColor(classificacao: string): {
  bg: string;
  text: string;
  border: string;
  ring: string;
} {
  switch (classificacao) {
    case 'Excelente':
      return { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', ring: 'ring-emerald-500' };
    case 'Bom':
      return { bg: 'bg-sky-50 dark:bg-sky-900/20', text: 'text-sky-700 dark:text-sky-400', border: 'border-sky-200 dark:border-sky-800', ring: 'ring-sky-500' };
    case 'Médio':
      return { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', ring: 'ring-amber-500' };
    case 'Alto':
      return { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800', ring: 'ring-orange-500' };
    case 'Crítico':
      return { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800', ring: 'ring-red-500' };
    default:
      return { bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700', ring: 'ring-slate-500' };
  }
}

export function getScoreGaugeColor(score: number): string {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#0ea5e9';
  if (score >= 40) return '#f59e0b';
  if (score >= 20) return '#f97316';
  return '#ef4444';
}
